/**
 * MIME allowlist + the upload-acceptance rule for dancer media, shared by the
 * browser uploader and both server-side create-upload-url actions.
 *
 * Isomorphic on purpose. The rest of the limits live in
 * `_lib/server/media-upload-limits.ts`, which is `server-only` — so the
 * browser-side pre-flight check in `dancer-document-upload-button.tsx` could
 * not import them and grew its own hardcoded `'application/pdf'` literal
 * instead. Two copies of one rule is exactly the drift Critical Rule #8 exists
 * to stop, so the rule lives here (no `server-only`) and the server module
 * re-exports it. There is still one edit surface; it just moved to a file both
 * runtimes can reach.
 *
 * BAL-747: `document` accepts a generic binary MIME when the file name ends in
 * `.pdf`. Some browsers report `application/octet-stream` for a perfectly valid
 * PDF (no OS-level mapping for the extension), and an exact-match gate
 * dead-ends those users with "Invalid file type for document". Relaxing the
 * gate here is safe because it is not the real check: the bytes are verified
 * against the `%PDF-` file signature at finalize
 * (`DancerMediaService.finalizeDirectUpload`), once they actually exist in
 * storage. This step runs before the browser has uploaded anything, so it can
 * only ever see client-supplied metadata.
 */

export const MEDIA_ALLOWED_MIME = {
  headshot: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
  photo: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
  video: ['video/mp4', 'video/quicktime', 'video/webm'],
  document: ['application/pdf'],
} as const;

export type MediaMimeBucket = keyof typeof MEDIA_ALLOWED_MIME;

/**
 * What a browser sends when it cannot identify the file. `''` only ever reaches
 * the client-side check (the server actions' Zod schema requires a non-empty
 * `mimeType`), but the predicate is shared, so it handles both.
 */
const GENERIC_BINARY_MIME_TYPES: readonly string[] = [
  'application/octet-stream',
  'binary/octet-stream',
  '',
];

/** Extensions accepted for `document` when the browser reports a generic MIME. */
const DOCUMENT_EXTENSIONS: readonly string[] = ['.pdf'];

/**
 * `accept` for a CV/document file input. The extension is listed alongside the
 * MIME for the same reason the server accepts a generic MIME: a browser with no
 * mapping for `application/pdf` would otherwise grey out every PDF in the
 * picker, so the user never even reaches the upload.
 */
export const DOCUMENT_ACCEPT_ATTRIBUTE = 'application/pdf,.pdf';

/**
 * Is this (mediaType, mimeType, fileName) triple allowed to start an upload?
 *
 * Exact MIME match for every bucket, plus the `document`-only generic-MIME
 * fallback described in the module docstring. `headshot` / `photo` / `video`
 * are untouched — a generic MIME is rejected there as before.
 */
export function isAllowedMediaUpload(input: {
  mediaType: MediaMimeBucket;
  mimeType: string;
  fileName: string;
}): boolean {
  const mimeType = input.mimeType.trim().toLowerCase();
  const allowed = MEDIA_ALLOWED_MIME[input.mediaType] as readonly string[];

  if (allowed.includes(mimeType)) return true;

  if (input.mediaType !== 'document') return false;
  if (!GENERIC_BINARY_MIME_TYPES.includes(mimeType)) return false;

  const fileName = input.fileName.trim().toLowerCase();
  return DOCUMENT_EXTENSIONS.some((extension) => fileName.endsWith(extension));
}

/**
 * The browser's `file.type`, made safe to send to the server.
 *
 * `File.type` is `''` whenever the browser cannot identify the file — the same
 * OS-mapping failure that produces `application/octet-stream`, and squarely
 * inside the cohort BAL-747 is about. The upload actions' Zod schemas require a
 * non-empty `mimeType`, so passing `''` through would clear the relaxed gate and
 * then fail validation with "String must contain at least 1 character(s)" — a
 * worse error than the one this issue set out to remove. Collapsing `''` to the
 * generic type keeps those files on the path the predicate already handles.
 */
export function normalizeBrowserMimeType(fileType: string): string {
  return fileType.trim() || 'application/octet-stream';
}

/**
 * The `Content-Type` the browser sends on the direct PUT to Supabase Storage.
 *
 * Not cosmetic, for two reasons:
 *
 * 1. The `dancer-media` bucket carries its own server-side `allowed_mime_types`
 *    (migration `20260501100000`), and `application/octet-stream` is NOT on it.
 *    PUTting the browser's generic type would be rejected by Storage with a 415
 *    — so relaxing the step-1 gate alone would move the failure rather than fix
 *    it. Sending `application/pdf` keeps the PUT inside the bucket allowlist
 *    without widening that allowlist, which is the safer of the two options.
 * 2. The stored object's content type is what Supabase serves back on the
 *    signed URL. `octet-stream` makes a browser download the CV as an opaque
 *    blob instead of opening it.
 *
 * Only claims `application/pdf` where `isAllowedMediaUpload` already agreed the
 * file is a `.pdf` document, and the claim is checked against the `%PDF-`
 * signature at finalize — an object that is not really a PDF is deleted there,
 * never retained under a type it does not deserve.
 */
export function resolveUploadContentType(input: {
  mediaType: MediaMimeBucket;
  mimeType: string;
  fileName: string;
}): string {
  const declared = input.mimeType.trim();

  if (
    input.mediaType === 'document' &&
    GENERIC_BINARY_MIME_TYPES.includes(declared.toLowerCase()) &&
    isAllowedMediaUpload(input)
  ) {
    return 'application/pdf';
  }

  return declared || 'application/octet-stream';
}
